import {
    AppBar,
    Box,
    IconButton,
    Toolbar,
    Button,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    TableHead, TableRow, TableCell, TableBody, Typography, Chip, Table, Link, DialogActions, Divider, Tooltip
} from '@mui/material'
import UserMenu from '../../components/Shared/UserMenu'
import React, {useContext, useEffect, useState} from 'react'
import { useNavigate } from 'react-router-dom'
import BreadCrumbs from './BreadCrumbs'
import { MenuOutlined, OpenInNew, AccountBalance, HomeWork, SupportAgent } from '@mui/icons-material'
import NotificationMenu from '../../components/Shared/NotificationMenu'
import BalanceBar from '../../components/Bank/BalanceBar'

import Context from "../../contexts/Context";
import {hasFeature} from "../../services/Main/AuthStorage";
import Masks from '../../components/Shared/Masks'
import moment from 'moment'
import StatusInvoice from "../../components/Invoice/StatusInvoice";

import TaskTasksPopoverButton from "../../pages/MainPages/TaskCenter/TaskPopover";

const drawerWidth = 250
const financialDrawerWidth = 260

// 👉 função para calcular o atraso e o tempo de bloqueio
const getInitialDelaySeconds = (user) => {
    if (!user?.overdue || !user?.invoices?.length) return 0

    const today = moment()
    let maxDaysOverdue = 0

    user.invoices.forEach((invoice) => {
        if (!invoice.due_date) return

        const dueDate = moment(invoice.due_date)
        const diffDays = today.diff(dueDate, 'days')

        // só considera se já venceu
        if (diffDays > 0 && diffDays > maxDaysOverdue) {
            maxDaysOverdue = diffDays
        }
    })

    // base 30s + 15s por dia de atraso
    const baseSeconds = 30
    const extraSeconds = maxDaysOverdue * 15

    return baseSeconds + extraSeconds
}

const Navbar = ({ handleDrawerToggle }) => {
    const { user, managerMode, toggleManagerMode } = useContext(Context)
    const navigate = useNavigate()
    const { perm_bank } = user || {}
    const isFinancialMode = managerMode === 'financial'

    const handleToggleMode = () => {
        toggleManagerMode()
        if (isFinancialMode) {
            navigate('/')
        } else {
            navigate('/financeiro/dashboard')
        }
    }
    const [openCompose, setOpenCompose] = useState(false)
    const [req, setReq] = useState(null)
    const [open, setOpen] = useState(false)
    const [disableClose, setDisableClose] = useState(false)
    const [countdown, setCountdown] = useState(0)
    const [anchorEl, setAnchorEl] = useState(null)

    const handleOpenPopover = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClosePopover = () => {
        setAnchorEl(null)
    }

    const openPopover = Boolean(anchorEl)

    // Abre o dialog e define o tempo inicial baseado no atraso
    useEffect(() => {
        if (user?.invoices?.length) {
            if (user?.overdue) {
                setOpen(true)
                const initialDelay = getInitialDelaySeconds(user)

                if (initialDelay > 0) {
                    setDisableClose(true)
                    setCountdown(initialDelay)
                } else {
                    setDisableClose(false)
                    setCountdown(0)
                }
            } else {
                // sem atraso: pode fechar na hora
                setDisableClose(false)
                setCountdown(0)
            }
        }
    }, [user])

    // Contagem regressiva enquanto o botão estiver desabilitado
    useEffect(() => {
        let timer

        if (open && disableClose) {
            timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer)
                        setDisableClose(false)
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)
        }

        return () => {
            if (timer) clearInterval(timer)
        }
    }, [open, disableClose])

    return (
        <Box sx={ user?.invoices ? { mb: 18 } : { mb: 10 }}>
            <AppBar
                position={'fixed'}
                sx={{
                    width: { md: `calc(100% - ${isFinancialMode ? financialDrawerWidth : drawerWidth}px)` },
                    ml: { md: `${isFinancialMode ? financialDrawerWidth : drawerWidth}px` },
                    bgcolor: 'white',
                    boxShadow: 'rgb(32 32 32 / 6%) 0px 8px 16px;',
                    transition: 'width 0.2s ease, margin-left 0.2s ease',
                }}>
                { user?.invoices &&
                    <Toolbar sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: user?.overdue ? '#dd7a7a' : '#d6b98f',
                        backgroundColor: user?.overdue ? '#FDEDEC' : '#FEFDEF'
                    }}>
                        {user?.overdue ?
                            <Box>
                                Olá {user?.first_name}, sua fatura venceu — efetue o pagamento para evitar o bloqueio da sua conta.
                                <Button onClick={()=> setOpen(true)}>Ver fatura</Button>
                            </Box>
                            :
                            <Box>
                                Olá {user?.first_name}, sua fatura referente ao BlocoB3 foi gerada.
                                <Button onClick={()=> setOpen(true)}>Ver fatura</Button>
                            </Box>
                        }
                    </Toolbar>}
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <IconButton
                        color="secondary"
                        aria-label="Abrir Barra"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuOutlined/>
                    </IconButton>
                    <BreadCrumbs/>
                    <Box display={'flex'} justifyContent={'flex-end'} alignItems={'center'}>
                        {hasFeature('cash_flow') &&
                            <Button
                                variant={isFinancialMode ? "contained" : "outlined"}
                                size="small"
                                onClick={handleToggleMode}
                                startIcon={isFinancialMode ? <HomeWork /> : <AccountBalance />}
                                sx={{
                                    mr: 1,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: 13,
                                    ...(isFinancialMode ? {
                                        bgcolor: '#1565C0',
                                        '&:hover': { bgcolor: '#0D47A1' },
                                    } : {
                                        borderColor: '#1565C0',
                                        color: '#1565C0',
                                        '&:hover': { borderColor: '#0D47A1', bgcolor: 'rgba(21, 101, 192, 0.04)' },
                                    })
                                }}
                            >
                                {isFinancialMode ? 'Gestão Imobiliária' : 'Gerenciador Financeiro'}
                            </Button>
                        }
                        <TaskTasksPopoverButton  />
                        { perm_bank === true &&
                            <Box onClick={() => setReq(prevState => !prevState)}
                           >
                                <BalanceBar req={req}/>
                            </Box>
                        }
                        <Button
                            component="a"
                            href="/suporte"
                            target="_blank"
                            rel="noopener"
                            variant="contained"
                            size="small"
                            startIcon={<SupportAgent />}
                            sx={{
                                mx: 1,
                                textTransform: 'none',
                                borderRadius: 2,
                                fontWeight: 600,
                                fontSize: 13,
                                bgcolor: '#475569',
                                color: '#fff',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#334155', boxShadow: 'none' },
                            }}
                        >
                            Suporte
                        </Button>
                        {/*<NotificationMenu/>*/}
                        <UserMenu/>
                    </Box>
                </Toolbar>
            </AppBar>

            { user?.invoices && (
                <Dialog
                    fullWidth
                    maxWidth={'lg'}
                    open={open}
                    scroll={'body'}
                >
                    <DialogTitle>Faturas ({user?.invoices.length})</DialogTitle>
                    <Divider/>
                    <DialogContent>
                        {user?.overdue &&
                            <Alert severity="error" sx={{ mb: 2 }} variant="outlined">
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Aviso importante sobre sua fatura vencida
                                </Typography>

                                <Typography variant="body1" gutterBottom>
                                    Identificamos que você possui faturas em atraso. Para garantir que todos os clientes mantenham
                                    suas contas sempre em dia, implementamos uma breve espera antes de permitir o fechamento
                                    desta janela.
                                </Typography>

                                <Typography variant="body1" gutterBottom>
                                    O tempo de espera é calculado conforme os <strong>dias de atraso</strong> da fatura,
                                    começando em <strong>30 segundos</strong> e adicionando <strong>15 segundos adicionais </strong>
                                    por dia vencido. Essa medida garante que você tenha a oportunidade de visualizar suas faturas
                                    e regularizar o pagamento antes que sua conta seja bloqueada.
                                </Typography>

                                <Typography variant="body1">
                                    Agradecemos pela compreensão e estamos sempre à disposição para ajudar no que for necessário.
                                </Typography>
                            </Alert>

                        }

                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Código</TableCell>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Vencimento</TableCell>
                                    <TableCell>Valor</TableCell>
                                    <TableCell>Boleto/Pix</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {user?.invoices.map(invoice => (
                                    <TableRow key={invoice.id}>
                                        <TableCell>{invoice.number}</TableCell>
                                        <TableCell>{invoice.name}</TableCell>
                                        <TableCell>
                                            <StatusInvoice invoice={invoice} />
                                        </TableCell>
                                        <TableCell>{moment(invoice.due_date).format("DD/MM/YYYY")}</TableCell>
                                        <TableCell>{Masks.money(invoice.total.toString())}</TableCell>
                                        <TableCell>
                                            <Link
                                                href={invoice.payment_link}
                                                target="_blank"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Chip
                                                    label="Página de pagamento"
                                                    size="small"
                                                    icon={<OpenInNew />}
                                                />
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </DialogContent>

                    <DialogActions sx={{ display:"flex", justifyContent:"center", padding: "2rem 1rem" }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setOpen(false)}
                            disabled={disableClose}
                        >
                            {disableClose ? `Fechar em (${countdown}s)` : 'Fechar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    )
}
export default Navbar
